import React, { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import PaymentForm from './components/PaymentForm';
import SmartRoutingDisplay from './components/SmartRoutingDisplay';
import PaymentStatus from './components/PaymentStatus';
import IntegrationPanel from './components/IntegrationPanel';
import Icon from '../../components/AppIcon';

const MerchantPaymentWidget = () => {
  const [paymentData, setPaymentData] = useState({
    selectedCrypto: 'usdc',
    amount: '',
    usdAmount: '',
    walletAddress: '',
    merchantId: 'merchant_demo_001'
  });

  const [paymentStatus, setPaymentStatus] = useState('idle'); // idle, processing, confirming, completed, failed
  const [routingInfo, setRoutingInfo] = useState(null);
  const [widgetConfig, setWidgetConfig] = useState({
    brandColor: '#3B82F6',
    merchantLogo: '',
    merchantName: 'Demo Merchant',
    enableQR: true,
    enableRateLock: true,
    theme: 'light'
  });

  const handlePaymentSubmit = (data) => {
    setPaymentData(data);
    setPaymentStatus('processing');
    
    // Simulate smart routing calculation
    setTimeout(() => {
      const routing = calculateSmartRouting(data);
      setRoutingInfo(routing);
      setPaymentStatus('confirming');
    }, 1500);

    // Simulate blockchain confirmation
    setTimeout(() => {
      setPaymentStatus('completed');
    }, 5000);
  };

  const calculateSmartRouting = (data) => {
    const amount = parseFloat(data?.usdAmount) || 0;
    
    // Smart routing logic based on transaction size and network conditions
    let selectedRail = 'lightning';
    let estimatedTime = '< 1 min';
    let networkFee = 0.50;
    let processingFee = amount * 0.015; // 1.5%
    
    if (data?.selectedCrypto === 'btc') {
      if (amount < 100) {
        selectedRail = 'lightning';
        estimatedTime = '< 1 min';
        networkFee = 0.50;
      } else {
        selectedRail = 'on-chain';
        estimatedTime = '10-30 min';
        networkFee = 2.50;
      }
    } else if (data?.selectedCrypto === 'eth') {
      selectedRail = 'layer-2';
      estimatedTime = '2-5 min';
      networkFee = 1.20;
    } else if (data?.selectedCrypto === 'usdc') {
      selectedRail = 'polygon';
      estimatedTime = '< 2 min';
      networkFee = 0.30;
    }

    return {
      selectedRail,
      estimatedTime,
      networkFee,
      processingFee,
      totalFee: networkFee + processingFee,
      rateLockedUntil: new Date(Date.now() + 600000)?.toISOString(), // 10 min lock
      confirmations: data?.selectedCrypto === 'btc' ? 3 : 12
    };
  };

  const resetPayment = () => {
    setPaymentStatus('idle');
    setRoutingInfo(null);
    setPaymentData({
      selectedCrypto: 'usdc',
      amount: '',
      usdAmount: '',
      walletAddress: '',
      merchantId: 'merchant_demo_001'
    });
  };

  const stats = [
    { label: 'Total Transactions', value: '12,847', change: '+18.5%', icon: 'TrendingUp', color: 'text-success' },
    { label: 'Total Volume', value: '$2.4M', change: '+24.3%', icon: 'DollarSign', color: 'text-primary' },
    { label: 'Avg Settlement Time', value: '2.3 min', change: '-15.2%', icon: 'Clock', color: 'text-warning' },
    { label: 'Success Rate', value: '99.8%', change: '+0.2%', icon: 'CheckCircle', color: 'text-success' }
  ];

  return (
    <MainLayout showControls={false}>
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="CreditCard" size={20} className="text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Merchant Payment Widget</h1>
              <p className="text-muted-foreground">Accept BTC, ETH, and stablecoins with smart routing</p>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats?.map((stat, index) => (
            <div key={index} className="card p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">{stat?.label}</span>
                <Icon name={stat?.icon} size={16} className={stat?.color} />
              </div>
              <div className="text-2xl font-bold text-foreground mb-1">{stat?.value}</div>
              <div className={`text-xs ${stat?.change?.startsWith('+') ? 'text-success' : 'text-error'}`}>
                {stat?.change} vs last month
              </div>
            </div>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Widget Preview */}
          <div className="lg:col-span-2 space-y-6">
            {/* Widget Container */}
            <div className="card p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-foreground">Payment Widget Preview</h2>
                  <p className="text-sm text-muted-foreground">Embedded checkout experience</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Powered by</span>
                  <span className="text-sm font-bold text-primary">BOLTPay</span>
                </div>
              </div>

              {/* Widget Simulation */}
              <div className="border-2 border-dashed border-border rounded-lg p-6 bg-muted/10">
                {paymentStatus === 'idle' && (
                  <PaymentForm 
                    onSubmit={handlePaymentSubmit}
                    config={widgetConfig}
                  />
                )}

                {(paymentStatus === 'processing' || paymentStatus === 'confirming') && routingInfo && (
                  <SmartRoutingDisplay 
                    paymentData={paymentData}
                    routingInfo={routingInfo}
                    status={paymentStatus}
                  />
                )}

                {paymentStatus === 'completed' && (
                  <PaymentStatus 
                    status="completed"
                    paymentData={paymentData}
                    routingInfo={routingInfo}
                    onReset={resetPayment}
                  />
                )}

                {paymentStatus === 'failed' && (
                  <PaymentStatus 
                    status="failed"
                    paymentData={paymentData}
                    routingInfo={routingInfo}
                    onReset={resetPayment}
                  />
                )}
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="card p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name="Zap" size={20} className="text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground">Smart Routing</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Automatically selects optimal payment rails based on transaction size, network congestion, and fees.
                </p>
              </div>

              <div className="card p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                    <Icon name="Lock" size={20} className="text-success" />
                  </div>
                  <h3 className="font-semibold text-foreground">Rate Lock</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Lock exchange rates for 10 minutes during checkout to protect against volatility.
                </p>
              </div>

              <div className="card p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                    <Icon name="QrCode" size={20} className="text-warning" />
                  </div>
                  <h3 className="font-semibold text-foreground">QR Payments</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Generate QR codes for mobile wallet payments with automatic address validation.
                </p>
              </div>

              <div className="card p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-error/10 flex items-center justify-center">
                    <Icon name="Shield" size={20} className="text-error" />
                  </div>
                  <h3 className="font-semibold text-foreground">Fraud Detection</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Real-time transaction monitoring with multi-signature security for high-value payments.
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Integration & Config */}
          <div className="space-y-6">
            <IntegrationPanel 
              config={widgetConfig}
              onConfigChange={setWidgetConfig}
            />
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default MerchantPaymentWidget;